import jwtDecode from 'jwt-decode';
import { fromUnixTime, differenceInMinutes } from 'date-fns';

import Auth from '@/store/api/system/auth';
import { fetchAccessToken, isRefreshInFlight } from '@/helpers';

// Apollo refreshes an expired token before a GraphQL request; nothing else in
// the app does. Every URL with `?token=`, every upload header and every
// direct axios call reads the stored token synchronously and would carry a
// stale one after a long idle (the morning tab). The keeper refreshes the
// token BEFORE it expires and the moment a tab comes back, so those readers
// find a live token without each of them having to ask.
//
// A refresh failing here is left to Apollo: its next request refreshes again
// or signs the session out with the usual handling.

const CHECK_INTERVAL_MS = 60 * 1000;
const REFRESH_AHEAD_MINUTES = 5;

const minutesLeft = () => {
    const token = Auth.getToken();
    if (!token) return null;

    try {
        const jwt = jwtDecode(token);
        return jwt && jwt.exp ? differenceInMinutes(fromUnixTime(jwt.exp), new Date()) : null;
    } catch (error) {
        return null;
    }
};

export const keepTokenFresh = () => {
    const left = minutesLeft();
    if (left === null || left > REFRESH_AHEAD_MINUTES || isRefreshInFlight()) return Promise.resolve();

    return fetchAccessToken().catch(() => null);
};

let started = false;

export const startTokenKeeper = () => {
    if (started || typeof window === 'undefined') return;
    started = true;

    window.setInterval(keepTokenFresh, CHECK_INTERVAL_MS);
    window.addEventListener('focus', keepTokenFresh);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') keepTokenFresh();
    });
};
